import { useState, useEffect, Fragment } from 'react';
import { observer } from 'mobx-react-lite';
import {
    Box,
    Button,
    CircularProgress,
    Divider,
    IconButton,
    List,
    ListItemButton,
    Popover,
    Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Selection } from '../selection';
import NodeStore from '../../../stores/node-store';
import NodePatchStore from '../../../stores/node-patch-store';
import { DbNode } from '../../../types/node';
import { deviceName, DeviceType } from '../../../types/device';

interface Props {
    selected: Selection;
    onSelect: (sel: Selection) => void;
}

const NodeListContent = observer(function NodeListContent({
    store,
    onPick,
}: {
    store: NodeStore;
    onPick: (sel: Selection) => void;
}) {
    if (store.loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 1.5 }}>
                <CircularProgress size={18} />
            </Box>
        );
    }

    const hasNodes = store.nodes.length > 0;
    const hasDevices = store.devices.length > 0;

    if (!hasNodes && !hasDevices) {
        return (
            <Box sx={{ px: 2, py: 1 }}>
                <Typography variant="caption" color="text.secondary">
                    Нет элементов
                </Typography>
            </Box>
        );
    }

    return (
        <List dense disablePadding>
            {store.nodes.map((node) => (
                <ListItemButton key={node.node_id} onClick={() => onPick({ kind: 'node', nodeId: node.node_id })}>
                    <Typography variant="body2">{node.name}</Typography>
                </ListItemButton>
            ))}
            {hasNodes && hasDevices && <Divider />}
            {store.devices.map((device) => {
                const info = device.info ?? {};
                const label = `${deviceName(info.deviceType as DeviceType)}-${device.device_id}`;
                return (
                    <ListItemButton
                        key={device.device_id}
                        onClick={() => onPick({ kind: 'device', deviceId: device.device_id, nodeId: device.node_id })}
                    >
                        <Typography variant="body2">{label}</Typography>
                    </ListItemButton>
                );
            })}
        </List>
    );
});

function BreadcrumbNodeRoot({ node, onPick }: { node: DbNode; onPick: (sel: Selection) => void }) {
    return (
        <Button
            size="small"
            onClick={() => onPick({ kind: 'node', nodeId: node.node_id })}
            sx={{ textTransform: 'none', minWidth: 0, px: 0.75, py: 0.25, fontSize: '0.8125rem' }}
        >
            {node.name}
        </Button>
    );
}

function BreadcrumbNode({
    node,
    parentNodeId,
    onPick,
}: {
    node: DbNode;
    parentNodeId: number;
    onPick: (sel: Selection) => void;
}) {
    const [anchor, setAnchor] = useState<HTMLButtonElement | null>(null);
    const [store] = useState(() => new NodeStore(parentNodeId));
    const [loaded, setLoaded] = useState(false);

    const handleArrowClick = (e: React.MouseEvent<HTMLButtonElement>) => {
        setAnchor(e.currentTarget);
        if (!loaded) {
            setLoaded(true);
            store.load().catch(() => {});
        }
    };

    return (
        <>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Button
                    size="small"
                    onClick={() => onPick({ kind: 'node', nodeId: node.node_id })}
                    sx={{ textTransform: 'none', minWidth: 0, px: 0.75, py: 0.25, fontSize: '0.8125rem' }}
                >
                    {node.name}
                </Button>
                <IconButton size="small" onClick={handleArrowClick} sx={{ p: 0.25 }}>
                    <ExpandMoreIcon sx={{ fontSize: '14px' }} />
                </IconButton>
            </Box>
            <Popover
                open={Boolean(anchor)}
                anchorEl={anchor}
                onClose={() => setAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            >
                <NodeListContent
                    store={store}
                    onPick={(sel) => {
                        onPick(sel);
                        setAnchor(null);
                    }}
                />
            </Popover>
        </>
    );
}

const BreadcrumbDevice = observer(function BreadcrumbDevice({
    selected,
}: {
    selected: Extract<Selection, { kind: 'device' }>;
}) {
    const [store] = useState(() => new NodeStore(selected.nodeId));

    useEffect(() => {
        store.loadDevices().catch(() => {});
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const device = store.devices.find((d) => d.device_id === selected.deviceId);
    const info = device?.info ?? {};
    const label = `${deviceName(info.deviceType as DeviceType)}-${selected.deviceId}`;

    return (
        <Typography variant="body2" sx={{ px: 0.75, fontSize: '0.8125rem' }}>
            {label}
        </Typography>
    );
});

const BreadcrumbPath = observer(function BreadcrumbPath({
    nodeId,
    selected,
    onSelect,
}: {
    nodeId: number;
    selected: Exclude<Selection, { kind: 'kosa' }>;
    onSelect: (sel: Selection) => void;
}) {
    const [store] = useState(() => new NodePatchStore(nodeId));
    const [error, setError] = useState(false);

    useEffect(() => {
        store.load().catch(() => setError(true));
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    if (error) {
        return (
            <Typography variant="caption" color="error">
                Ошибка загрузки пути
            </Typography>
        );
    }

    if (store.loading) {
        return <CircularProgress size={14} />;
    }

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0 }}>
            {store.nodes.map((node, i) => (
                <Fragment key={node.node_id}>
                    {i > 0 && (
                        <Typography sx={{ color: 'text.disabled', userSelect: 'none', fontSize: '0.8125rem' }}>
                            /
                        </Typography>
                    )}
                    {i === 0 ? (
                        <BreadcrumbNodeRoot node={node} onPick={onSelect} />
                    ) : (
                        <BreadcrumbNode node={node} parentNodeId={store.nodes[i - 1].node_id} onPick={onSelect} />
                    )}
                </Fragment>
            ))}
            {selected.kind === 'device' && (
                <>
                    {store.nodes.length > 0 && (
                        <Typography sx={{ color: 'text.disabled', userSelect: 'none', fontSize: '0.8125rem' }}>
                            /
                        </Typography>
                    )}
                    <BreadcrumbDevice selected={selected} />
                </>
            )}
        </Box>
    );
});

export default function Selector({ selected, onSelect }: Props) {
    if (selected.kind === 'kosa') return null;

    return <BreadcrumbPath key={selected.nodeId} nodeId={selected.nodeId} selected={selected} onSelect={onSelect} />;
}
